/**
 * ProtectedRoute.jsx — Route-level authentication and authorization guards.
 *
 * Exports two guard components:
 *
 *   ProtectedRoute — Renders children only when the user passes all enabled
 *                    checks. Redirects otherwise.
 *                    Checks (in order):
 *                      1. Auth: user must be logged in (requireAuth=true by default).
 *                      2. Admin: user must have role=admin (requireAdmin=false by default).
 *                      3. Approval: user must have status=approved (requireApproved=true
 *                         by default). Admins are exempt from this check.
 *                    While the initial auth check is in progress (loading=true), a
 *                    centered spinner is displayed instead of making any redirect decision.
 *
 *   PublicRoute   — Renders children only when the user is NOT authenticated.
 *                   If authenticated, redirects to:
 *                     - location.state.from (the page the user came from), or
 *                     - the explicit redirectTo prop if provided, or
 *                     - /admin for admins, /dashboard for everyone else.
 *                   Useful for login and signup pages so authenticated users
 *                   cannot revisit them.
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2 } from 'lucide-react';

/**
 * ProtectedRoute — Guards a route behind authentication and optional role/status checks.
 *
 * @param {React.ReactNode} props.children       - Content to render if all checks pass.
 * @param {boolean}         props.requireAuth     - Require the user to be logged in. Default: true.
 * @param {boolean}         props.requireAdmin    - Require role=admin. Default: false.
 * @param {boolean}         props.requireApproved - Require status=approved. Default: true.
 *                                                  Admins bypass this check automatically.
 * @param {string}          [props.redirectTo]    - Override the default redirect destination.
 */
export function ProtectedRoute({
  children,
  requireAuth = true,
  requireAdmin = false,
  requireApproved = true,
  redirectTo,
}) {
  const { loading, isAuthenticated, isAdmin, isApproved } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Check authentication
  if (requireAuth && !isAuthenticated) {
    // Redirect to login, preserving the intended destination
    return <Navigate to={redirectTo || '/login'} state={{ from: location }} replace />;
  }

  // Check admin requirement
  if (requireAdmin && !isAdmin()) {
    return <Navigate to={redirectTo || '/unauthorized'} replace />;
  }

  // Check approval status (skip for admin)
  if (requireApproved && !isAdmin() && !isApproved()) {
    return <Navigate to={redirectTo || '/unauthorized'} replace />;
  }

  return children;
}

/**
 * PublicRoute — Prevents authenticated users from accessing public-only pages.
 *
 * If the user is already logged in, they are redirected away. If the user came
 * from a protected page (stored in location.state.from), they are sent back there.
 * Otherwise, the explicit redirectTo prop is used if given, or role-based defaults
 * apply (/admin for admins, /dashboard for regular users).
 *
 * @param {React.ReactNode} props.children    - Content to render for unauthenticated users.
 * @param {string}          [props.redirectTo] - Where to send authenticated users.
 *                                              When provided, overrides role-based defaults.
 */
export function PublicRoute({ children, redirectTo }) {
  const { loading, isAuthenticated, isAdmin } = useAuth();
  const location = useLocation();

  // Show loading spinner while auth state is still being determined.
  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    const from = location.state?.from?.pathname;
    // If an explicit redirectTo is provided, always use it (ignores admin role).
    // Otherwise fall back to role-based default: admins → /admin, others → /dashboard.
    const roleDefault = isAdmin() ? '/admin' : '/dashboard';
    const destination = from || (redirectTo ?? roleDefault);
    return <Navigate to={destination} replace />;
  }

  return children;
}
